import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api.js'
import { Drawer, DrawerSection, Field, Notice } from './ui.js'

/**
 * Entering a gateway's own API credentials.
 *
 * ── Why the form refuses to be optimistic ───────────────────────────────────
 *
 * The API verifies these against the gateway before it stores them, and this
 * panel is built around that: nothing is "saved" here, it is *submitted for
 * checking*, and the failure comes back in the gateway's own terms — a wrong
 * mode, a salt that does not match the key, an id from the other environment.
 *
 * That matters because the failure this replaces was silent. A mistyped secret
 * stores fine, the checkout opens fine, the order row is written fine, and the
 * shopper is refused on the gateway's own page. Nothing on this side knows.
 *
 * ── Nothing is ever read back ───────────────────────────────────────────────
 *
 * The fields start empty every time, including for a gateway that is already
 * configured. There is no endpoint that returns a stored secret and there
 * should not be: a screen that can show one is a screen that hands it to
 * whoever borrows the tab. Replacing a credential means typing the whole pair
 * again, which is the correct cost.
 */

export type GatewayKey = 'razorpay' | 'cashfree' | 'payu'

const TITLE: Record<GatewayKey, string> = {
  razorpay: 'Razorpay',
  cashfree: 'Cashfree',
  payu: 'PayU',
}

export function GatewayKeysDrawer({
  gateway,
  onClose,
}: {
  gateway: GatewayKey
  onClose: () => void
}): React.ReactNode {
  const queryClient = useQueryClient()

  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [webhook, setWebhook] = useState('')
  const [mode, setMode] = useState(gateway === 'cashfree' ? 'sandbox' : 'test')
  const [failed, setFailed] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api('/v1/gateways/credentials', { method: 'PUT', body: { gateway, ...body } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateways'] })
      onClose()
    },
  })

  async function submit(): Promise<void> {
    setFailed(null)

    const body: Record<string, string> =
      gateway === 'razorpay'
        ? { keyId: a, keySecret: b, ...(webhook.trim() ? { webhookSecret: webhook } : {}) }
        : gateway === 'cashfree'
          ? { appId: a, secretKey: b, mode }
          : { merchantKey: a, salt: b, mode }

    try {
      await save.mutateAsync(body)
    } catch (cause) {
      setFailed(cause instanceof ApiError ? cause.message : 'Those credentials could not be checked.')
    }
  }

  const labels =
    gateway === 'razorpay'
      ? { a: 'Key ID', b: 'Key secret', aHint: 'Starts with rzp_live_ or rzp_test_.' }
      : gateway === 'cashfree'
        ? { a: 'App ID', b: 'Secret key', aHint: 'From Cashfree, under Developers → API Keys.' }
        : { a: 'Merchant key', b: 'Salt', aHint: 'From PayU, under Payment Gateway → Key/Salt.' }

  return (
    <Drawer open title={`${TITLE[gateway]} credentials`} onClose={onClose}>
      <DrawerSection title="Keys">
        <Notice kind="info" title="Checked before they are stored">
          These are sent to {TITLE[gateway]} to confirm they work. Nothing is saved unless {TITLE[gateway]} accepts them.
        </Notice>

        {failed && (
          <Notice kind="warn" title={`${TITLE[gateway]} did not accept these`}>
            {failed}
          </Notice>
        )}

        <Field label={labels.a} hint={labels.aHint}>
          <input className="input" value={a} onChange={(event) => setA(event.target.value)} autoComplete="off" />
        </Field>

        <Field label={labels.b} hint="Never shown again once saved.">
          <input
            className="input"
            type="password"
            value={b}
            onChange={(event) => setB(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {gateway === 'razorpay' && (
          <Field
            label="Webhook secret"
            hint="Optional. It is checked against a webhook body, so it cannot be verified here — leave it blank to keep the current one."
          >
            <input
              className="input"
              type="password"
              value={webhook}
              onChange={(event) => setWebhook(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
        )}

        {gateway !== 'razorpay' && (
          <Field label="Mode" hint="The keys belong to one mode. The wrong one is rejected rather than half-working.">
            <select className="input" value={mode} onChange={(event) => setMode(event.target.value)}>
              <option value={gateway === 'cashfree' ? 'sandbox' : 'test'}>Test</option>
              <option value="production">Production</option>
            </select>
          </Field>
        )}

        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={save.isPending || !a.trim() || !b.trim()}
          >
            {save.isPending ? `Checking with ${TITLE[gateway]}…` : 'Check and save'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </DrawerSection>
    </Drawer>
  )
}
